import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import { Partner } from '@bybeam/platform-types';
import { Theme, createTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import colors from './base-colors.theme.js';

// NOTE: This theming is _only_ for direct use of material UI components.
// If we can get away with simple markup and tailwind styling, that is the
// preferred approach. However, if a MUI component has exactly what we need
// and saves time/effort, this is an acceptable alternative.
const BASE_THEME_OBJECT = {
  components: {
    MuiInputLabel: {
      styleOverrides: {
        formControl: {
          color: colors.text,
          transform: 'translate(16px, 13px) scale(1)',

          '&.Mui-error': {
            color: colors.text,
          },
          '&.Mui-disabled': {
            color: colors.disabledLabel,
          },
          '&.Mui-focused': {
            color: colors.text,
          },
        },
        shrink: {
          transform: 'translate(16px, 6px) scale(0.75)',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiFilledInput-root': {
            paddingTop: '15px',

            '& .MuiFilledInput-input': {
              paddingBottom: '6px',
              paddingLeft: '8px',
            },
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          color: colors.text,
          height: '1.25rem',
          lineHeight: '1.25rem',
          paddingRight: '16px',
          paddingLeft: '16px',
          paddingBottom: '6px',
          paddingTop: '22px',

          '&.MuiInputBase-inputMultiline': {
            paddingLeft: '4px',
            paddingRight: '4px',
            paddingBottom: 0,
            paddingTop: 0,
          },

          '&.Mui-disabled': {
            WebkitTextFillColor: colors.disabledLabel,
          },
        },

        root: {
          backgroundColor: colors.inputBackground,
          borderRadius: '4px',
          borderColor: colors.inputBorder,
          borderWidth: '1px',

          '&:hover': {
            backgroundColor: colors.inputBackground,
            '&:not(.Mui-disabled):before': {
              borderBottomWidth: 0,
            },
          },
          '&.Mui-focused, &.Mui-disabled': {
            backgroundColor: colors.inputBackground,
          },
          // Firefox focus color workaround
          '&.Mui-focused': {
            outline: '2px auto Highlight',
          },
          '&.Mui-focused:not(.Mui-disabled)': {
            outline: '2px auto -webkit-focus-ring-color',
          },
          '&:before, &:after': {
            borderBottomWidth: 0,
          },
          '&.Mui-error': {
            borderColor: colors.error,
          },
          '& .MuiButtonBase-root': {
            marginRight: '-8px',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: colors.text,
          marginLeft: '16px',
          marginRight: '16px',
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: colors.text,
        },
        root: {
          alignItems: 'flex-start',
          borderRadius: '2px',
          cursor: 'pointer',
          marginLeft: '0px',
          marginRight: '0px',
          paddingBottom: '0.75rem',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem',
          paddingTop: '0.75rem',
          width: '100%',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: 'transparent',
          minHeight: '1.25rem',
        },

        icon: {
          color: colors.text,

          '&.Mui-disabled': {
            color: colors.textSecondary,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: 10,
          paddingBottom: 10,

          '&:focus': {
            outline: 'thick auto #005FCC',
            outlineOffset: '-4px',
          },

          '&.Mui-disabled': {
            backgroundColor: colors.white,
            color: colors.disabledLabel,
            opacity: 1,
          },

          '&.Mui-selected:not(.Mui-disabled)': {
            backgroundColor: colors.text,
            color: colors.white,
          },
          'span.MuiButtonBase-root.MuiCheckbox-root.MuiCheckbox-colorPrimary.Mui-checked': {
            color: colors.white,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          marginTop: '-9px',
          color: colors.primary,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0',
          paddingTop: '3px',
          paddingRight: '9px',
          '&:hover': {
            backgroundColor: 'transparent',
          },

          '.MuiTouchRipple-root': {
            top: '2px',
            right: '10px',
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: colors.selectedItem,
          color: colors.text,
          borderRadius: 0,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          '&[data-popper-placement*="bottom"]': {
            '&& .MuiTooltip-tooltip': { marginTop: 6 },
          },
        },
        tooltip: {
          backgroundColor: colors.activeLabel,
          borderRadius: 2,
          padding: '8px 16px',
          fontSize: '.75rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: colors.tableBackground,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize' as const,
          color: colors.textSecondary,
          '&.Mui-selected': { color: colors.text },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: colors.tableBackground },
        sizeSmall: { height: '20px', width: 'fit-content' },
        sizeMedium: { height: '24px', width: 'fit-content' },
        icon: { fontSize: '16px' },
      },
    },
  },
  palette: {
    primary: { main: colors.primary },
    error: { main: colors.error },
    text: {
      primary: colors.text,
      secondary: colors.textSecondary,
    },
    info: { main: colors.link },
  },
  typography: {
    fontFamily: ['SofiaPro', 'sans-serif'].join(','),
  },
};

const useMUItheme = (partner: Partner): Theme => {
  const [theme, setTheme] = useState<Theme>(createTheme(BASE_THEME_OBJECT));

  useEffect(() => {
    if (partner?.whitelabeling?.brandColor) {
      const MUItheme = createTheme(
        mergeObjects([
          BASE_THEME_OBJECT,
          {
            components: {
              MuiTabs: {
                styleOverrides: {
                  indicator: { backgroundColor: partner.whitelabeling.brandColor ?? colors.brand },
                },
              },
            },
          },
        ]),
      );
      setTheme(MUItheme);
    }
  }, [partner]);

  return theme;
};

export default useMUItheme;
